// Lightweight Markdown renderer with footnote-style citation markers for the
// Collection Story Builder. Supports: # ## ### headings, paragraphs, > blockquotes,
// - / * bullet lists, 1. ordered lists, **bold**, *italic*, [text](url), and
// [^c:CITATION_ID] citation markers that resolve to numbered footnotes.

export const formatCitation = (c) => {
  if (!c) return "";
  const parts = [];
  if (c.title) parts.push(c.title);
  if (c.publisher) parts.push(c.publisher);
  if (c.date_published) parts.push(c.date_published);
  if (c.page_or_timecode) parts.push(c.page_or_timecode);
  return parts.join(", ");
};

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const CITE_RE = /\[\^c:([^\]]+)\]/g;
const FIG_RE = /^\[\[(figure|plate|detail|image|annotation|reverse|closeup)\s*:\s*(.+?)\]\]$/i;

export const slugifyHeading = (t) =>
  String(t || "")
    .toLowerCase()
    .replace(/\[\^c:[^\]]+\]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);

// Extract a table of contents (## and ### headings) from Markdown.
export const extractToc = (markdown) => {
  const toc = [];
  (markdown || "").split("\n").forEach((ln) => {
    const h = /^(#{2,3})\s+(.*)$/.exec(ln);
    if (h) {
      const text = h[2].replace(CITE_RE, "").replace(/\*\*/g, "").trim();
      toc.push({ level: h[1].length, text, id: slugifyHeading(h[2]) });
    }
  });
  return toc;
};

const buildCiteMap = (markdown, citations) => {
  const byId = new Map((citations || []).map((c) => [c.id, c]));
  const citeMap = new Map();
  const ordered = [];
  let m;
  CITE_RE.lastIndex = 0;
  while ((m = CITE_RE.exec(markdown || "")) !== null) {
    const id = m[1].trim();
    if (!byId.has(id)) continue;
    if (!citeMap.has(id)) {
      const n = citeMap.size + 1;
      citeMap.set(id, n);
      ordered.push({ n, cite: byId.get(id) });
    }
  }
  return { citeMap, ordered };
};

const inlineFmt = (rawText, citeMap) => {
  let t = escapeHtml(rawText);
  // citation markers first (before link syntax)
  t = t.replace(CITE_RE, (mm, id) => {
    const n = citeMap.get(id.trim());
    if (!n) return "";
    return `<sup class="fn-ref"><a href="#note-${n}" id="ref-${n}">${n}</a></sup>`;
  });
  // links [text](url)
  t = t.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // bold then italic
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
  return t;
};

export const renderArticleHTML = (markdown, citations) => {
  if (!markdown || !markdown.trim()) return "";
  const { citeMap, ordered } = buildCiteMap(markdown, citations);
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let i = 0;
  let figN = 0;

  const isBlank = (l) => l.trim() === "";

  while (i < lines.length) {
    const line = lines[i];

    if (isBlank(line)) { i++; continue; }

    // Figure / plate placeholder (block-level)
    const fig = FIG_RE.exec(line.trim());
    if (fig) {
      figN += 1;
      const kind = fig[1].toLowerCase();
      const label = kind === "plate" ? "Plate" : "Figure";
      out.push(
        `<figure class="fig-placeholder"><div class="fig-box"><span class="fig-tag">${label} ${figN}</span></div>` +
        `<figcaption>${label} ${figN}. ${inlineFmt(fig[2], citeMap)}</figcaption></figure>`
      );
      i++;
      continue;
    }

    // Heading
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length; // 1->h2, 2->h3, 3->h4
      const tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";
      const id = slugifyHeading(h[2]);
      out.push(`<${tag} id="${id}">${inlineFmt(h[2], citeMap)}</${tag}>`);
      i++;
      continue;
    }

    // Blockquote (consecutive > lines)
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      out.push(`<blockquote>${inlineFmt(buf.join(" "), citeMap)}</blockquote>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        buf.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      out.push(`<ol>${buf.map((b) => `<li>${inlineFmt(b, citeMap)}</li>`).join("")}</ol>`);
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        buf.push(lines[i].replace(/^[-*]\s+/, ""));
        i++;
      }
      out.push(`<ul>${buf.map((b) => `<li>${inlineFmt(b, citeMap)}</li>`).join("")}</ul>`);
      continue;
    }

    // Paragraph (consecutive non-blank, non-special lines)
    const para = [];
    while (
      i < lines.length &&
      !isBlank(lines[i]) &&
      !/^(#{1,3})\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !FIG_RE.test(lines[i].trim())
    ) {
      para.push(lines[i]);
      i++;
    }
    out.push(`<p>${inlineFmt(para.join(" "), citeMap)}</p>`);
  }

  if (ordered.length) {
    out.push('<hr class="notes-sep" />');
    out.push('<h3 class="notes-title">Notes</h3>');
    out.push('<ol class="notes">');
    for (const { n, cite } of ordered) {
      const link = cite.url
        ? ` <a href="${escapeHtml(cite.url)}" target="_blank" rel="noopener noreferrer">[source]</a>`
        : "";
      out.push(`<li id="note-${n}">${escapeHtml(formatCitation(cite))}${link}</li>`);
    }
    out.push("</ol>");
  }

  return out.join("\n");
};
